import { BaseViewStore } from 'core/stores';
import { OrderStore } from 'modules/order/stores';
import { ReturnLabelForm } from 'modules/order/forms';
import { observable, action, makeObservable } from 'mobx';
import { ModalParams } from 'core/models';
import { CarrierServiceStore } from 'modules/carrier/stores';
import { RateServiceStore } from 'modules/rate/stores';
import { ShipmentInfoReturnStore } from 'modules/shipment-info-return/stores';
import { lookupRouteName, orderStatus, sourceOfChange } from 'common/constants';
import { LookupStore } from 'modules/lookup/stores';
import { translate, createFileUrl, formatDate, mapCarrierServiceWithRates, PiiDataValidator } from 'core/utils';
import { WarehouseOrganizationInfoStore } from 'modules/warehouse/stores';
import { ReturnAddressStore } from 'modules/return-address/stores';
import { DimensionsStepForm as BoxForm } from '../forms/labeling-mode';
import { BaasicDropdownViewStore } from 'common/stores';
import * as printJS from 'print-js';
import _ from 'lodash';

class ReturnLabelViewStore extends BaseViewStore {
	@observable selectedOrder;
	@observable disableSaveBtn = true;
	@observable isFormValid = false;
	@observable carrierServiceOptions = [];
	@observable tempCarrierServiceOptions = [];
	@observable pageCarrierService = 1;
	@observable isCarrierServiceLoading = false;
	@observable isCreateLabelLoading = false;
	@observable infiniteSearchPhrase = null;
	@observable pdfURLReturn = null;
	@observable pageReturnReason = 1;
	@observable isReturnReasonLoading = false;
	@observable selectedReturnReason = null;
	@observable returnReasonOptions = [];
	@observable tempReasonOptions = [];
	@observable boxOptions = [];
	@observable tempBoxOptions = [];
	@observable isBoxDropdownLoading = false;
	@observable pageBox = 1;
	@observable debouncedBoxSearch = null;
	@observable isBoxRequired = false;
	@observable noRatesMessage;
	@observable onePerBoxMessage;
	activeShipmentInfoReturn = null;
	debouncedCarrierServiceSearch = null;
	debouncedGetRates = null;
	selectedCarrier = null;
	packageInformation = null;
	isEdit = false;
	seller;
	defaultReturnAddress = null;

	form = new ReturnLabelForm({
		onSuccess: async () => {
			await this.createReturnLabel();
		},
	});

	boxForm = new BoxForm({
		onSuccess: async () => {
			await this.onCreateTemplateSubmit();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.orderStore = new OrderStore(rootStore);
		this.carrierServiceStore = new CarrierServiceStore(rootStore);
		this.rateServiceStore = new RateServiceStore(rootStore);
		this.shipmentInfoReturnStore = new ShipmentInfoReturnStore(rootStore);
		this.warehouseOrganizationInfoStore = new WarehouseOrganizationInfoStore(rootStore);
		this.returnAddressStore = new ReturnAddressStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.piiDataValidator = new PiiDataValidator(rootStore);
		this.initialize(this.rootStore.routerStore.routerState.params.id);
	}

	@action.bound
	initialize = async orderId => {
		this.suspend();
		await this.getOrder(orderId);
		await this.getDefaultReturnAddress();
		if (this.defaultReturnAddress != null) {
			this.form.$('returnAddress').value = {
				value: this.defaultReturnAddress.addressId,
				label: this.defaultReturnAddress.name,
			};
		}
		this.returnAddressDropDownStore = new BaasicDropdownViewStore({
			field: this.form.$('returnAddress'),
			store: this.returnAddressStore,
			itemTemplate: {
				value: 'addressId',
				label: 'name',
			},
			filterProperties: {
				sellerIds: [this.selectedOrder.seller.id],
				embed: ['seller', 'address', 'address.coreCountry', 'address.coreCountryState'],
			},
			isRequired: true,
			onChangeCallback: async field => {
				this.form.$('returnAddress').value = {
					value: field.value,
					label: field.label,
				};
				this.form.$('carrierService').set('rules', '');
				await this.validateForm();
				if (this.isFormValid) {
					await this.getRates();
				}
				this.form.$('carrierService').set('rules', 'required');
			},
		});
		this.seller = this.selectedOrder.seller;
		await Promise.all([this.getReturnReasons(), this.updateForm(this.selectedOrder)]);
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.calculateCheckedQuantity(this.selectedOrder);
		this.debouncedCarrierServiceSearch = _.debounce((searchPhrase, { action }) => {
			this.searchCarrierServiceAsync(searchPhrase, { action });
		}, 500);
		this.debouncedGetRates = _.debounce(() => {
			this.getRates();
		}, 700);
		this.debouncedBoxSearch = _.debounce((searchPhrase, { action }) => {
			this.searchBoxAsync(searchPhrase, { action });
		}, 500);
		await this.getBoxOptions();

		if (this.selectedOrder.box != null) {
			this.boxForm.$('box').value = {
				value: this.selectedOrder.box.id,
				label: this.selectedOrder.box.name,
				extra: this.selectedOrder.box,
			};
		}

		this.boxForm.$('box').set('rules', this.isBoxRequired ? 'required' : '');
		this.resume();
	};

	@action.bound
	getOrder = async id => {
		this.selectedOrder = await this.orderStore.getOrder(id, {
			embed: [
				'channel',
				'channel.channelType',
				'box',
				'seller',
				'shippingCoreCountry',
				'shippingCoreCountryState',
				'carrierService',
				'carrier',
				'orderLines',
				'orderLines.product',
				'orderStatus',
				'shipmentInfoReturns',
				'shipmentInfoReturns.returnedItems',
				'shipmentInfoReturns.returnedItems.product',
			],
		});
		this.selectedOrder.orderLines = _.filter(this.selectedOrder.orderLines, orderLine => {
			return orderLine.quantity > 0 && orderLine.productId;
		});
	};

	@action.bound
	getReturnReasons = async () => {
		await this.lookupStore.find({ page: 1, rpp: 50 }, lookupRouteName.returnReason);
		this.returnReasonOptions = _.map(this.lookupStore.data, item => {
			const reasonItem = { label: item.name, value: item.id };
			return reasonItem;
		});
	};

	@action.bound
	async handleOnCarrierServiceMenuScroll() {
		if (this.carrierServiceOptions.length <= this.pageCarrierService * 45) return;
		this.isCarrierServiceLoading = true;
		this.pageCarrierService++;
		await this.carrierServiceStore.findCarrierService({
			page: this.pageCarrierService,
			rpp: 45,
		});
		this.tempCarrierServiceOptions = _.map(this.carrierServiceStore.data, item => {
			return { label: item.name, value: item.id };
		});
		if (this.tempCarrierServiceOptions.length > 0) {
			this.carrierServiceOptions = this.carrierServiceOptions.concat(this.tempCarrierServiceOptions);
			this.scrollLoadCarrierService = false;
		}
		this.isCarrierServiceLoading = false;
	}

	@action.bound
	async searchCarrierServiceAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageCarrierService = 1;
		}
		if (action === 'input-change') {
			this.isCarrierServiceLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageCarrierService = 1;
			this.carrierServiceOptions = [];
			let filter = {
				search: searchPhrase || null,
				rpp: 45,
				page: 1,
			};
			await this.carrierServiceStore.findCarrierService(filter);
			const newOptions = [];
			_.forEach(this.carrierServiceStore.data, item => {
				newOptions.push({ label: item.name, value: item.id });
			});
			this.carrierServiceOptions = newOptions;
			this.isCarrierServiceLoading = false;
		}
	}

	@action.bound
	async getCarrierServices() {
		await this.carrierServiceStore.findCarrierService({
			page: 1,
			rpp: 25,
		});
		this.carrierServiceOptions = _.map(this.carrierServiceStore.data, item => {
			return { label: item.name, value: item.id };
		});
	}

	@action.bound
	getDefaultReturnAddress = async () => {
		await this.returnAddressStore.findReturnAddress({
			sellerIds: [this.selectedOrder.seller.id],
			page: 1,
			rpp: 25,
			isDefaultReturnAddress: true,
			embed: ['seller', 'address', 'address.coreCountry', 'address.coreCountryState'],
		});
		this.defaultReturnAddress = this.returnAddressStore.data[0];
	};

	editModal = new ModalParams({
		onClose: async () => {
			this.suspend();
			await this.returnAddressDropDownStore.reload();
			this.resume();
		},
	});

	@action.bound
	updateForm = async () => {
		this.suspend();
		let formData = this.piiDataValidator.checkIfUncodedPiiDataExists(this.selectedOrder) ? this.selectedOrder : null;
		if (this.selectedOrder.orderStatus.abrv !== orderStatus.pending) {
			// Reverses shipper and recipient information. If the value is not provided, set an empty string.
			this.form.$('buyerName').value = formData.buyerName || '';
			this.form.$('phone1').value = formData.buyerPhone || '';
			this.form.$('address1').value = formData.shippingAddress1 || '';
			this.form.$('address2').value = formData.shippingAddress2 || '';
			this.form.$('address3').value = formData.shippingAddress3 || '';
			this.form.$('city').value = formData.shippingCity || '';
			this.form.$('zipCode').value = formData.shippingZipCode || '';
			this.form.$('countryCode').value = formData.shippingCoreCountry.countryCode || '';
			this.form.$('countryStateCode').value = formData.shippingCoreCountryState.countryStateCode || '';
		}

		if (this.selectedOrder.orderLines.length === 1) {
			if (this.selectedOrder.orderLines[0].product.onePerBox) {
				this.selectedOrder.orderLines[0].isChecked = true;
				this.selectedOrder.orderLines[0].isPartiallyChecked = false;
				this.selectedOrder.orderLines[0].checkedQuantity = 1;
				this.onePerBoxMessage = 'ORDERVIEW.RETURNLABELVIEW.ONEPERBOXMESSAGE';
				this.isBoxRequired = false;
			} else {
				this.onePerBoxMessage = '';
				this.selectedOrder.orderLines[0].checkedQuantity = this.selectedOrder.orderLines[0].quantity;
				if (this.selectedOrder.orderLines[0].checkedQuantity > 1) {
					this.isBoxRequired = true;
				}
			}
		}

		// Initial carrier service value.
		this.form.$('carrierService').value = {
			value: null,
			label: `${translate('ORDERVIEW.RETURNLABELVIEW.ALLFIELDSREQUIRED')}`,
		};

		await this.validateForm();
		this.resume();
	};

	@action.bound
	async validateForm() {
		await this.form.validate().then(({ isValid }) => {
			if (!isValid) {
				this.isFormValid = isValid;
				this.form.showErrors();
			} else {
				this.isFormValid = isValid;
			}
		});
	}

	@action.bound
	createReturnLabel = async () => {
		this.suspend();
		this.isCreateLabelLoading = true;
		try {
			const { carrierService, returnReasonId, internalNote, returnAddress } = this.form.values();
			const shipperInformation = this.composeShipperInformation();
			// Find selected carrier service in available options to get rate from extra property.
			const selectedCarrierServiceRate = _.find(
				this.carrierServiceOptions,
				item =>
					item.value.carrierServiceId === carrierService.value.carrierServiceId &&
					(carrierService.value.carrierId == null ||
						(carrierService.value.carrierId != null && carrierService.value.carrierId === item.value.carrierId))
			);
			const assignedCarrierId =
				selectedCarrierServiceRate &&
				selectedCarrierServiceRate.extra != null &&
				selectedCarrierServiceRate.extra.carrier != null
					? selectedCarrierServiceRate.extra.carrier.id
					: selectedCarrierServiceRate.extra != null && selectedCarrierServiceRate.extra.carrierId != null
					? selectedCarrierServiceRate.extra.carrierId
					: null;
			this.packageInformation = this.isBoxRequired
				? {
						boxId: this.boxForm.$('box').value.value,
						weight: this.selectedOrder.orderLines
							.filter(i => i.checkedQuantity > 0)
							.reduce((acc, curr) => acc + curr.product.weight * curr.checkedQuantity, 0),
				  }
				: undefined;
			const addressId = returnAddress.value;
			const addressName = returnAddress.label;
			const response = await this.shipmentInfoReturnStore.createReturnLabel({
				orderId: this.selectedOrder.id,
				shipperInformation: shipperInformation,
				carrierServiceId: carrierService.value.carrierServiceId,
				carrierId: assignedCarrierId,
				internalNote,
				returnReasonId,
				addressId: addressId,
				addressName: addressName,
				packageInformation: this.packageInformation,
				itemsForReturn: this.getItemsForReturn(),
			});
			this.rootStore.notificationStore.success([
				'ORDERVIEW.RETURNLABELVIEW.SUCCESS',
				{ id: this.selectedOrder.orderNumber },
			]);
			if (response) {
				const label = response.shipmentData.label;
				this.pdfURLReturn = createFileUrl(label);
				printJS({
					printable: this.pdfURLReturn,
					onPrintDialogClose: () => {
						this.rootStore.routerStore.goBack();
					},
				});
			}
		} catch (err) {
			this.rootStore.notificationStore.error([
				'ORDERVIEW.RETURNLABELVIEW.FAIL',
				{ id: this.selectedOrder.orderNumber },
			]);
		}
		this.isCreateLabelLoading = false;
		this.resume();
	};

	onBack = () => {
		this.rootStore.routerStore.goBack();
	};

	onCarrierServiceSelect = item => {
		if (item != null) {
			this.form.$('carrierService').value = item;
			this.selectedOrder.assignedCarrierServiceId = item.value.carrierServiceId;
			this.selectedOrder.assignedCarrierId = item.value.carrierId;
			this.selectedCarrierService = item;
			this.selectedCarrierService.id = item.value.carrierServiceId;
			this.selectedCarrierService.name = item.name;
		} else {
			this.form.$('carrierService').value = '';
			this.selectedCarrierService = null;
		}
	};

	@action.bound
	async onReturnReasonSelect(item) {
		if (item != null) {
			this.form.$('returnReasonId').value = item.value;
			this.selectedReturnReason = item;
			this.form.$('carrierService').set('rules', '');
			await this.validateForm();
			if (this.isFormValid) {
				await this.getRates();
			}
			this.form.$('carrierService').set('rules', 'required');
		} else {
			this.form.$('returnReasonId').value = '';
			this.selectedReturnReason = null;
		}
	}

	@action.bound
	onAddressDataChange = async () => {
		this.form.$('carrierService').set('rules', '');
		await this.validateForm();
		if (this.isFormValid) {
			this.debouncedGetRates();
		}
		this.form.$('carrierService').set('rules', 'required');
	};

	@action.bound
	getRates = async (searchPhrase = null) => {
		// If none of the items is selected, do not calcualte rate since data is missing.
		if (!_.every(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity === 0)) {
			this.noRatesMessage = '';
			this.isCarrierServiceLoading = true;
			this.form.$('carrierService').value = {
				value: null,
				label: `${translate('ORDERVIEW.RETURNLABELVIEW.CALCULATING')}`,
			};
			this.form.$('carrierService').set('rules', 'required');
			let options = [];
			const address = this.form.$('returnAddress').value;
			const shipperInformation = this.composeShipperInformation();
			this.packageInformation = this.isBoxRequired
				? {
						boxId: this.boxForm.$('box').value.value,
						weight: this.selectedOrder.orderLines
							.filter(i => i.checkedQuantity > 0)
							.reduce((acc, curr) => acc + curr.product.weight * curr.checkedQuantity, 0),
				  }
				: undefined;

			let selectedProductIds = [];
			_.forEach(this.selectedOrder.orderLines, orderLine => {
				if (orderLine.checkedQuantity > 0) {
					selectedProductIds.push(orderLine.productId);
				}
			});

			if (address !== null) {
				await this.rateServiceStore.findCarrierServiceWithRates({
					rateSearchRest: { search: searchPhrase, rpp: 45 },
					orderId: this.selectedOrder.id,
					addressId: address.value,
					shipperInformation: shipperInformation,
					channelId: this.selectedOrder.channelId,
					isReturn: true,
					packageInformationRest: this.packageInformation,
					itemsForReturn: this.getItemsForReturn(),
				});
				options = mapCarrierServiceWithRates(this.rateServiceStore.data);
				options = _.sortBy(options, ['label']);
			}
			this.tempCarrierServiceOptions = [...options];
			this.carrierServiceOptions = this.tempCarrierServiceOptions;
			this.isCarrierServiceLoading = false;

			if (this.carrierServiceOptions.length > 0 && !this.isCarrierServiceLoading) {
				// If rates exist, the cheapest is auto selected.
				const cheapestCarrierService = _.orderBy(
					this.carrierServiceOptions,
					['extra.rateComposition.total'],
					['asc']
				)[0];
				this.form.$('carrierService').value = {
					value: cheapestCarrierService.value,
					label: cheapestCarrierService.label,
				};
			} else if (
				this.carrierServiceOptions.length == 0 &&
				selectedProductIds.length > 1 &&
				!this.isCarrierServiceLoading
			) {
				// Else if rates exist and multiple SKUs are selected, message is displayed inidicating there is no rate or no matching carrier service per virtual SKU.
				this.form.$('carrierService').value = null;
				this.noRatesMessage = 'ORDERVIEW.RETURNLABELVIEW.NOMATCHINGCARRIERS';
			} else if (!this.isCarrierServiceLoading) {
				// Else no rate message is displayed.
				this.form.$('carrierService').value = null;
				this.noRatesMessage = 'ORDERVIEW.RETURNLABELVIEW.NORATES';
			}
		}
	};

	composeShipperInformation = () => {
		const {
			buyerName,
			phone1,
			address1,
			address2,
			address3,
			city,
			zipCode,
			countryStateCode,
			countryCode,
		} = this.form.values();

		const shipperInformation = {
			personName: buyerName,
			phoneNumber: phone1,
			streetLines: [address1, address2, address3].filter(value => value), // Removes empty string, null or undefined from the array.
			city: city,
			countryCode: countryCode,
			stateOrProvinceCode: countryStateCode,
			postalCode: zipCode,
		};

		return shipperInformation;
	};

	@action.bound
	handleFocusChange = async (item, isBlur) => {
		let matchingItem = this.selectedOrder.orderLines.find(i => i.sku === item.sku);
		if (isBlur) {
			if (matchingItem.checkedQuantity == '') {
				matchingItem.checkedQuantity = 0;
			}
		} else {
			if (matchingItem.checkedQuantity == 0) {
				matchingItem.checkedQuantity = '';
			}
		}
	};

	@action.bound
	handleItemQuantityChange = async (selectedItem, isIncrease, event) => {
		_.forEach(this.selectedOrder.orderLines, orderLine => {
			const isOnePerBox = orderLine.product.onePerBox;
			if (selectedItem.sku === orderLine.sku && selectedItem.lineNumber === orderLine.lineNumber) {
				orderLine.disabled = false;
				if (isOnePerBox && isIncrease && !event) {
					// One per box = true, quantity increase.
					orderLine.checkedQuantity = 1;
					orderLine.disabled = false;
					orderLine.isChecked = isIncrease;
				} else if (isOnePerBox && !isIncrease && !event) {
					// One per box = true, quantity decrease.
					orderLine.checkedQuantity = 0;
					orderLine.disabled = false;
					orderLine.isChecked = false;
					orderLine.isPartiallyChecked = false;
				} else if (!isOnePerBox && isIncrease && !event) {
					// One per box = false, quantity increase.
					orderLine.checkedQuantity = ++orderLine.checkedQuantity;
					if (orderLine.checkedQuantity > orderLine.quantity) {
						orderLine.checkedQuantity = orderLine.quantity;
					}
					orderLine.isChecked = orderLine.checkedQuantity === orderLine.quantity;
					orderLine.isPartiallyChecked =
						orderLine.checkedQuantity !== orderLine.quantity && orderLine.checkedQuantity > 0;
				} else if (!isOnePerBox && !isIncrease && !event) {
					// One per box = false, quantity decrease.
					orderLine.checkedQuantity = --orderLine.checkedQuantity;
					if (orderLine.checkedQuantity < 0) {
						orderLine.checkedQuantity = 0;
					}
					orderLine.isChecked = orderLine.checkedQuantity === orderLine.quantity;
					orderLine.isPartiallyChecked =
						orderLine.checkedQuantity !== orderLine.quantity && orderLine.checkedQuantity > 0;
				} else if (isOnePerBox && event) {
					// One per box = true, checkbox increase/decrease.
					orderLine.checkedQuantity = isIncrease ? 1 : 0;
					orderLine.isChecked = isIncrease;
					orderLine.isPartiallyChecked = false;
				} else if (!isOnePerBox && event) {
					// One per box = false, checkbox increase/decrease.
					orderLine.checkedQuantity = isIncrease ? orderLine.quantity : 0;
					orderLine.isChecked = isIncrease;
					orderLine.isPartiallyChecked = false;
				}

				if (
					(isOnePerBox && orderLine.isChecked && this.selectedOrder.orderLines.length > 1) ||
					(this.selectedOrder.orderLines.length === 1 && isOnePerBox && orderLine.isChecked)
				) {
					this.onePerBoxMessage = 'ORDERVIEW.RETURNLABELVIEW.ONEPERBOXMESSAGE';
				} else {
					this.onePerBoxMessage = '';
				}
			} else {
				if (isOnePerBox) {
					if (!isIncrease) {
						orderLine.disabled = false;
					} else {
						orderLine.disabled = true;
					}
				} else if (!selectedItem.product.onePerBox) {
					orderLine.disabled = false;
				} else {
					orderLine.disabled = true;
				}
			}
		});

		// If one of the items selected is not one per box (OnePerBox = false), all one per box items should be disabled.
		// If none of the items is selected, none is disabled for selections.
		this.checkSelectedItems();

		// Box is required if at least one item is selected, else it is hidden, not matter if OnePerBox = true.
		this.isBoxRequired = _.sum(_.map(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity)) > 1;

		if (this.isFormValid && _.some(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity !== 0)) {
			this.debouncedGetRates();
		}
	};

	@action.bound
	handleItemQuantityOnInputChange = async (item, value) => {
		let getNewRates = false;
		let matchingItem = this.selectedOrder.orderLines.find(i => i.sku === item.sku && i.lineNumber === item.lineNumber);
		if (!value) {
			getNewRates = true;
			matchingItem.checkedQuantity = 0;
		}
		if (value >= 0 && value <= matchingItem.quantity) {
			getNewRates = true;
			matchingItem.checkedQuantity = value;
		}
		matchingItem.isPartiallyChecked =
			matchingItem.checkedQuantity !== matchingItem.quantity && matchingItem.checkedQuantity > 0;
		matchingItem.isChecked = matchingItem.checkedQuantity === matchingItem.quantity;

		// If one of the items selected is not one per box (OnePerBox = false), all one per box items should be disabled.
		// If none of the items is selected, none is disabled for selections.
		this.checkSelectedItems();

		// Box is required if more then one quantity is selected.
		this.isBoxRequired = _.sum(_.map(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity)) > 1;

		if (
			this.isFormValid &&
			getNewRates &&
			_.some(this.selectedOrder.orderLines, item => {
				return item.checkedQuantity != 0;
			})
		) {
			this.debouncedGetRates();
		}
	};

	@action.bound
	onBoxChange = async (value, field) => {
		this.form.$('carrierService').set('rules', '');
		if (value && value.label === 'Add new template') {
			this.showCreateTemplate = true;
			field.set(null);
			this.boxForm.$('box').resetValidation();
			this.boxForm.$('box').set('rules', '');
		} else {
			this.boxForm.$('box').set('rules', this.isBoxRequired ? 'required' : '');
			field.set(value);
			if (value) {
				this.showCreateTemplate = false;
				await this.validateForm();
				if (
					this.isFormValid &&
					_.some(this.selectedOrder.orderLines, item => {
						return item.checkedQuantity != 0;
					})
				) {
					await this.getRates();
				}
			} else {
				this.isFormValid = false;
			}
		}
	};

	@action.bound
	async onMenuClose() {
		this.pageBox = 1;
		this.infiniteSearchPhrase = null;
		await this.getBoxOptions();
	}

	@action.bound
	async getBoxOptions() {
		await this.lookupStore.find({ page: 1, rpp: 15 }, lookupRouteName.box);
		this.boxOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
	}

	@action.bound
	async searchBoxAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.page = 1;
		}
		if (action === 'input-change') {
			this.isBoxDropdownLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageBox = 1;
			this.boxOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
			};
			await this.lookupStore.find(filter, lookupRouteName['box']);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.boxOptions = newOptions;
			this.isBoxDropdownLoading = false;
		}
	}

	@action.bound
	async handleOnBoxMenuScroll() {
		if (this.boxOptions.length < this.pageBox * 15) return;
		this.isBoxDropdownLoading = true;
		this.pageBox++;
		await this.lookupStore.find(
			{ search: this.infiniteSearchPhrase, page: this.pageBox, rpp: 15 },
			lookupRouteName['box']
		);
		this.tempBoxOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (this.tempBoxOptions.length > 0) {
			this.boxOptions = this.boxOptions.concat(this.tempBoxOptions);
		}
		this.isBoxDropdownLoading = false;
	}

	@action.bound
	onCreateTemplateCancel = () => {
		this.boxForm.reset();
		this.boxForm.clear();
		this.boxForm.$('box').invalidate();
		this.showCreateTemplate = false;
		this.boxForm.$('box').set('rules', this.isBoxRequired ? 'required' : '');
	};

	@action.bound
	onCreateTemplateSubmit = async () => {
		try {
			const { name, boxWidth, boxHeight, boxLength } = this.boxForm.values();
			const boxValues = {
				name,
				boxWidth,
				boxHeight,
				boxLength,
			};
			const response = await this.lookupStore.createLookup(boxValues, lookupRouteName.box, sourceOfChange.createLookup);
			if (response.message) {
				this.boxForm.$('name').invalidate(response.message);
			} else {
				var createdBox = this.lookupStore.item;
				if (createdBox) {
					this.showCreateTemplate = false;
					this.boxForm.$('name').clear();
					this.boxForm.$('boxHeight').clear();
					this.boxForm.$('boxWidth').clear();
					this.boxForm.$('boxLength').clear();
					this.boxOptions.push({ value: createdBox.id, label: createdBox.name, extra: createdBox });
					this.boxForm.$('box').value = {
						value: createdBox.id,
						label: createdBox.name,
						extra: createdBox,
					};
					this.boxForm.$('box').set('rules', this.isBoxRequired ? 'required' : '');
					await this.validateForm();
					if (this.isFormValid) {
						await this.getRates();
					}
				}
			}
		} catch (err) {}
	};

	@action.bound
	calculateCheckedQuantity = order => {
		_.forEach(order.orderLines, orderLine => {
			orderLine.isChecked = order.orderLines.length == 1 ? true : false;
			orderLine.isPartiallyChecked = false;
			// If is one per box.
			if (order.orderLines.length == 1 && orderLine.product.onePerBox) {
				orderLine.checkedQuantity = 1;
			} else if (order.orderLines.length == 1 && !orderLine.product.onePerBox) {
				orderLine.checkedQuantity = orderLine.quantity;
				// If is not one per box.
			} else {
				orderLine.checkedQuantity = 0;
			}
		});

		// Box is required if at least one item is selected, else it is hidden, not matter if OnePerBox = true.
		this.isBoxRequired = _.sum(_.map(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity)) > 1;
	};

	@action.bound
	checkSelectedItems = () => {
		if (
			_.some(this.selectedOrder.orderLines, orderLine => !orderLine.product.onePerBox && orderLine.checkedQuantity > 0)
		) {
			_.forEach(this.selectedOrder.orderLines, orderLine => {
				if (orderLine.product.onePerBox) {
					orderLine.disabled = true;
				}
			});
		} else if (_.every(this.selectedOrder.orderLines, orderLine => orderLine.checkedQuantity === 0)) {
			_.forEach(this.selectedOrder.orderLines, orderLine => {
				orderLine.disabled = false;
			});
		}
	};

	@action.bound
	getItemsForReturn = () => {
		const itemsForReturn = [];
		_.forEach(this.selectedOrder.orderLines, orderLine => {
			if (orderLine.checkedQuantity > 0) {
				itemsForReturn.push({
					productId: orderLine.productId,
					quantity: orderLine.checkedQuantity,
					product: orderLine.product,
					lineNumber: orderLine.lineNumber,
				});
			}
		});
		return itemsForReturn;
	};
}

export default ReturnLabelViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/ReturnLabelViewStore.js